/**
 * @fileoverview Email input with live format check + MX verification.
 * @module ui-admin/components
 *
 * Responsibilities:
 * - Render a controlled <input type="email"> that performs live format checks
 *   on every keystroke and triggers MX-record verification on blur.
 * - Surface validation status visually with checkmark / X / spinner / offline icons.
 *
 * Dependencies:
 * - lucide-react (peer)
 * - useEmailValidation from ../utils/emailValidation
 *
 * Architecture:
 * - Presentation-only React component; no Dexie or service access.
 * - The MX call is issued by the underlying hook to /api/validate/email through
 *   the host app's reverse proxy.
 *
 * Notes:
 * - Promoted from web/tenant-web/src/components/ValidatedEmailInput.tsx and
 *   web/system-web/src/components/ValidatedEmailInput.tsx (near-identical
 *   copies). The tenant-web variant with offline-state handling is the
 *   canonical version preserved here.
 */

import React, { useCallback, useRef } from 'react';
import { CheckCircle, XCircle, Loader2, WifiOff } from 'lucide-react';
import { useEmailValidation } from '../utils/emailValidation';

interface ValidatedEmailInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  required?: boolean;
}

/**
 * @component ValidatedEmailInput
 *
 * Purpose:
 * - Render a controlled email input with live format and async MX validation.
 *
 * Props:
 * - value: Current email string (controlled).
 * - onChange: Called with the next string on each change.
 * - placeholder/className/disabled/id/name/required: Standard input passthroughs.
 *
 * Behavior:
 * - On change → format check → updates state; if domain changed and format valid, triggers MX check.
 * - On blur (and format valid) → MX check via /api/validate/email.
 * - Renders status icon to the right of the input.
 *
 * Data Source:
 * - local + remote (MX check fetches the host app's validate endpoint)
 *
 * Constraints:
 * - Stateless wrt persistence; caller owns the value/onChange contract.
 *
 * Performance:
 * - In-flight MX request is aborted on subsequent change.
 * - MX validation automatically re-triggers when domain changes.
 */
export default function ValidatedEmailInput({
  value,
  onChange,
  placeholder = 'user@example.com',
  className,
  disabled,
  id,
  name,
  required,
}: ValidatedEmailInputProps) {
  const { formatValid, mxStatus, mxError, checkFormat, checkMx, reset } = useEmailValidation();
  const previousDomainRef = useRef<string>('');

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onChange(v);
    if (v && v.trim()) {
      const isFormatValid = checkFormat(v);
      
      // Extract domain to detect changes
      const currentDomain = v.includes('@') ? v.split('@')[1]?.toLowerCase() : '';
      const previousDomain = previousDomainRef.current;
      
      // If domain changed and format is valid, re-trigger MX validation
      if (currentDomain !== previousDomain && isFormatValid && currentDomain) {
        checkMx(v.trim());
      }
      
      previousDomainRef.current = currentDomain;
    } else {
      reset();
      previousDomainRef.current = '';
    }
  }, [onChange, checkFormat, checkMx, reset]);

  const handleBlur = useCallback(() => {
    if (value && value.trim() && formatValid) {
      checkMx(value.trim());
    }
  }, [value, formatValid, checkMx]);

  const showStatus = value && value.trim().length > 0;
  const isChecking = mxStatus === 'checking';
  const isValid = mxStatus === 'valid';
  const isInvalid = mxStatus === 'invalid';
  const isError = mxStatus === 'error';
  const isOffline = mxStatus === 'offline';

  return (
    <div className="relative">
      <input
        type="email"
        id={id}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-9'}
      />
      {showStatus && (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
          {isChecking && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
          {isValid && <CheckCircle className="w-4 h-4 text-green-500" />}
          {(isInvalid || isError) && <XCircle className="w-4 h-4 text-red-500" />}
          {isOffline && <WifiOff className="w-4 h-4 text-gray-400" />}
          {!isChecking && !isValid && !isInvalid && !isError && !isOffline && formatValid && (
            <span className="w-4 h-4 block" />
          )}
        </span>
      )}
      {(isInvalid || isError) && mxError && (
        <p className="mt-1 text-xs text-red-600">{mxError}</p>
      )}
      {isOffline && mxError && (
        <p className="mt-1 text-[10px] text-gray-400 flex items-center gap-1">
          <WifiOff className="w-3 h-3 shrink-0" />
          {mxError}
        </p>
      )}
    </div>
  );
}
